import api, { fetchData } from './api';
import Paginacao from '../types/paginacao';
import { isNumber } from '@mui/x-data-grid/internals';

export interface CargoResponse {
  id: number;
  clientName: string;
  client: any;
  supplier: any;
  from: any;
  supplierName: string;
  weight: number;
  weightRaw?: string;
  pieces: number;
  dawb: string;
  pu: Date;
  wrNumber: string;
  arrivalDate: string;
  cargoContents: CargoContent[];
  carrierInformations: CarrierInformations[];
  dimensions: Dimensions[];
  user: User;
  input: string;
  poNumber: number;
  soNumber: number;
  description: string;
  dgr: boolean;
  dgrGoods: boolean;
}
export interface CargoReportResponse {
  id: number;
  clientName: string;
  fromIdentification: string;
  supplierName: string;
  weight: number;
  weightRaw?: string;
  pieces: number;
  dawb: string;
  pu: Date;
  puFormatted: string;
  wrNumber: string;
  poNumber: string;
  soNumber: string;
  description: string;
  invoice: string;
  shippedBy: string;
  destination: string;
  to: string;
  releasedBy: string;
  released: Date;
  releasedFormatted: string;
  master: string;
  house: string;
  shipDate: Date;
  shipDateFormatted: string;
  inputDate: Date;
  inputDateFormatted: string;
  arrival: Date;
  arrivalFormatted: string;
}

export interface CargoDocumentTypeResponse {
  id: number;
  key: string;
  description: string;
}
interface CargoListFilter {
  release: boolean;
  poNumber?: string;
  dawb?: string;
  invoice?: string;
  description?: string;
  wrNumber?: string;
  supplier?: string;
  userID?: number;
  from: string;
  arrivalDateInicio: Date;
  arrivalDateFim: Date;
  paginacao: Paginacao;
  order?: string;
  sort?: string;
  pageNum: string;
  quantityPerPage: string;
  client?: string;
}
interface CargoReportListFilter {
  userID?: number;
  situation: string;
  from: string;
  client: string;
  situationDateInicio: Date;
  situationDateFim: Date;
  paginated: boolean;
  paginacao: Paginacao;
  order?: string;
  sort?: string;
  pageNum: string;
  quantityPerPage: string;
}

interface User {
  id: number;
  fullName: string;
}
export interface Dimensions {
  id: number;
  quantity: number;
  height: number;
  width: number;
  length: number;
}

interface CarrierInformations {
  id: number;
  invoice: string;
  document: Document;
  cargo: any;
}

interface CargoContent {
  id: number;
  contentId: number;
  content: Content;
}

interface Document {
  id: number;
  key: string;
  description: string;
}

interface Content {
  id: number;
  description: string;
  type: string;
  file: string;
}

export const getListaCargo = async (
  filters: CargoListFilter,
  released?: boolean
) => {
  try {
    const endpoint: string = '/CargoInfo/list';
    const knownKeys = new Set([
      'release', 'paginacao', 'pageNum', 'quantityPerPage',
      'order', 'sort', 'userID',
    ]);

    const dynamicFilters: Record<string, any> = {};
    if (filters && typeof filters === 'object') {
      for (const [key, value] of Object.entries(filters)) {
        if (!knownKeys.has(key)) {
          dynamicFilters[key] = value;
        }
      }
    }

    const params: Record<string, any> = {
      release: released,
      // Precisa ir com o prefixo "paginacao." - sem ele o backend nunca recebia sort/order
      // (FiltroBuscarListaCargoVM so tem Paginacao.Sort/Paginacao.Order, nao um Sort/Order
      // solto), entao clicar num cabecalho de coluna pra ordenar nao tinha efeito nenhum.
      "paginacao.order": filters?.paginacao?.order,
      "paginacao.sort": filters?.paginacao?.sort,
      ...dynamicFilters,
      suppliername: filters?.supplier ?? dynamicFilters?.supplier ?? dynamicFilters?.suppliername,
      "paginacao.pageNum": (isNumber(Number(filters?.pageNum))) ? Number(filters?.pageNum) + 1 : 1,
      "paginacao.quantityPerPage": filters?.quantityPerPage || "40",
    };

    for (const key of Object.keys(params)) {
      const v = params[key];
      if (v === undefined || v === null || v === '') {
        delete params[key];
      }
    }

    const response = await fetchData<CargoResponse>(endpoint, params);

    return response;
  } catch (error) {
    console.error('Erro ao buscar lista de cargos:', error);
    throw error;
  }
};

export const getReportListaCargo = async (
  filters: CargoReportListFilter
) => {
  try {
    const endpoint: string = '/CargoInfo/report/list';
    const params: Record<string, any> = {
      situation: filters?.situation,
      from: filters?.from,
      client: filters?.client,
      situationDateInicio: filters?.situationDateInicio,
      situationDateFim: filters?.situationDateFim,
      paginated: filters?.paginated,
      "paginacao.pageNum": Number(filters?.pageNum) + 1,
      "paginacao.quantityPerPage": filters?.quantityPerPage,
      // O backend lê Order/Sort de dentro do objeto Paginacao (FiltroBuscarListaReportCargoVM
      // não tem Order/Sort soltos), então precisam ir como "paginacao.order"/"paginacao.sort".
      // Antes iam soltos ("order"/"sort") e o model binding do ASP.NET nunca preenchia
      // Paginacao.Order/Paginacao.Sort, fazendo o "order by" da grid de Report não funcionar.
      "paginacao.order": filters?.order,
      "paginacao.sort": filters?.sort,
    };

    for (const key of Object.keys(params)) {
      if(!params[key] && 
        typeof params[key] !== 'boolean' && 
        (typeof params[key] === 'string' && params[key].length === 0) 
      ) {
        delete params[key];
      }
    }

    const response = await fetchData<CargoReportResponse>(endpoint, params);

    return response;
  } catch (error) {
    console.error('Erro ao buscar lista de cargos:', error);
    throw error;
  }
};

export const getCargoById = async (id: number) => {
  try {
    const response = await api.get(`/CargoInfo/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao buscar cargo com ID ${id}`, error);
    throw error;
  }
};
export const deleteCagro = async (id: number) => {
  try {
    const response = await api.delete(`/CargoInfo/delete/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Erro ao buscar cargo com ID ${id}`, error);
    throw error;
  }
};

export const getCargoDocumentType = async () => {
  try {
    const endpoint: string = 'CargoInfo/CargoDocumentType/all';
    const response = await fetchData<CargoDocumentTypeResponse>(endpoint, {});
    return response.data;
  } catch (error) {
    console.error(`Erro ao buscar cargo document type`, error);
    throw error;
  }
};

export const postCargo = async (payload: FormData) => {
  try {
    const response = await api.post('CargoInfo/create', payload, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.status;
  } catch (error) {
    console.error('Erro ao cadastrar cargo:', error);
    throw error;
  }
};

export const editcargo = async (id: number, payload: FormData): Promise<number> => {
  try {
    const response = await api.put(`/CargoInfo/update/${id}`, payload, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.status;
  } catch (error: any) {
    console.error('Error updating cargo:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    throw error;
  }
};